import { NavLink } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { ProductItems, LayoutItems, PriceItems, UserItems, SalesReceiptItems, BottomItems } from 'config/MenuItems'
import { LIMITED_MODE_ENABLED } from 'config/limitedMode'
import { CreditCardIcon, LayoutIcon, ProductIcon, SaleIcon, UserIcon } from 'components/svg'
import SubMenu from './ExpanableMenu';

interface SideBarProps {
  customClass?: string;
  navOpen?: boolean;
  setNavClose?(): void;
}

const SideBar = (props: SideBarProps) => {
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return

    const checkScrollable = () => {
      const { scrollHeight, clientHeight, scrollTop } = el
      const hasMoreBelow = scrollHeight > clientHeight + 1 && scrollTop + clientHeight < scrollHeight - 2
      setShowScrollIndicator((prev) => (prev === hasMoreBelow ? prev : hasMoreBelow))
    }

    checkScrollable()
    const observer = new ResizeObserver(checkScrollable)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    el.addEventListener('scroll', checkScrollable, { passive: true })
    window.addEventListener('resize', checkScrollable)

    return () => {
      observer.disconnect()
      el.removeEventListener('scroll', checkScrollable)
      window.removeEventListener('resize', checkScrollable)
    }
  }, [])

  return (
    <aside className={`bg-gray-700 shadow-lg w-52 min-w-max bg-opacity-60 md:h-screen transition-all ease duration-200 h-full ${props.customClass}`}>
      <div className='justify-between flex flex-col h-full'>
        {/* Header */}
        <div className="flex items-center justify-center h-16 flex-shrink-0">
          <h1 className="font-bold text-white">Kupid Admin</h1>
        </div>

        {/* Scrollable Menu Content */}
        <div className={`flex-1 overflow-hidden ${showScrollIndicator ? 'scroll-fade-bottom' : ''}`}>
          <div
            ref={scrollContainerRef}
            className='h-full overflow-y-auto overflow-x-hidden scrollbar-none'
          >
            <ul className="flex flex-col pb-4">
              {LIMITED_MODE_ENABLED ? (
                SalesReceiptItems.map((item) => (
                  <li key={item.routeName} onClick={props.setNavClose}>
                    <NavLink
                      to={item.routeName}
                      className="flex items-center py-2 pl-3 text-center text-sm rounded-lg mx-2 my-0.5 text-white opacity-80 hover:opacity-100 hover:bg-gray-300 hover:bg-opacity-20"
                      activeClassName="bg-gray-800 opacity-80"
                    >
                      {item.icon}
                      <span className="ml-3 text-gray-200">{item.title}</span>
                    </NavLink>
                  </li>
                ))
              ) : (
                <>
                  <SubMenu
                    title="Products & Categories"
                    icon={ProductIcon}
                    items={ProductItems}
                    initialExpand={true}
                    setNavClose={props.setNavClose || (() => {})}
                  />
                  <SubMenu
                    title="Button Styles & Layout"
                    icon={LayoutIcon}
                    items={LayoutItems}
                    initialExpand={true}
                    setNavClose={props.setNavClose || (() => {})}
                  />
                  <SubMenu
                    title="Pricing"
                    icon={CreditCardIcon}
                    items={PriceItems}
                    initialExpand={true}
                    setNavClose={props.setNavClose || (() => {})}
                  />
                  <SubMenu
                    title="Sales/Receipt"
                    icon={SaleIcon}
                    items={SalesReceiptItems}
                    initialExpand={true}
                    showItemIcons={false}
                    setNavClose={props.setNavClose || (() => {})}
                  />
                  <SubMenu
                    title="Users & Settings"
                    icon={UserIcon}
                    items={UserItems}
                    setNavClose={props.setNavClose || (() => {})}
                    initialExpand={true}
                  />
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Menu */}
        <div className='border-t border-gray-600 border-opacity-40 py-1.5 flex-shrink-0'>
          <ul>
            {BottomItems.map((item) => (
              <li key={item.routeName} onClick={props.setNavClose}>
                <NavLink
                  to={item.routeName}
                  className="flex items-center py-2 pl-3 text-center text-sm rounded-lg mx-2 my-0.5 text-white opacity-80 hover:opacity-100 hover:bg-gray-300 hover:bg-opacity-20"
                  activeClassName="bg-gray-800 opacity-80"
                >
                  {item.icon}
                  <span className="ml-3 text-gray-200">{item.title}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  )
}

export default SideBar